import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Line } from "@react-three/drei";
import ResponsiveText from "./ResponsiveText";
import * as THREE from "three";
import { SCENE_COUNT } from "../constants";
import { getSoftCircleTexture } from "../softCircleTexture";

// One tracked particle: its rest point and radial direction. It oscillates about
// this fixed spot — it never travels with the wave.
const REST = [2.6, 1.5];
const REST_R = Math.hypot(REST[0], REST[1]);
const REST_DIR = [REST[0] / REST_R, REST[1] / REST_R];
const TRACK_RANGE = 0.4; // visual oscillation extent for the guide line

// Sound is a LONGITUDINAL pressure wave. A field of particles (the "medium") sits
// at rest; a source at the centre pushes its neighbours, which push theirs, so the
// disturbance ripples outward as rings of COMPRESSION (bunched, bright) and
// RAREFACTION (spread, dim). Each particle only oscillates about its rest spot
// along the radial direction — nothing actually travels the distance, only the
// vibration does. That is the whole idea of "what sound is".
const GX = 110;
const GY = 64;
const COUNT = GX * GY;
const WIDTH = 17;
const HEIGHT = 10;
const K = 2.1; // spatial frequency of the pressure rings
const SPEED = 3.0;

const dim = new THREE.Color("#0a2236");
const bright = new THREE.Color("#8ff2ff");

export default function Scene01Birth({ position, scroll, sceneIndex }) {
  const pointsRef = useRef();
  const sourceRef = useRef();
  const trackedRef = useRef();
  const sprite = useMemo(() => getSoftCircleTexture(), []);

  // Rest grid: store rest x,y and the radial unit direction + radius for each point.
  const { positions, colors, rest, dir } = useMemo(() => {
    const positions = new Float32Array(COUNT * 3);
    const colors = new Float32Array(COUNT * 3);
    const rest = new Float32Array(COUNT * 2);
    const dir = new Float32Array(COUNT * 3); // dx, dy, radius
    let i = 0;
    for (let gy = 0; gy < GY; gy++) {
      for (let gx = 0; gx < GX; gx++) {
        const x = (gx / (GX - 1) - 0.5) * WIDTH;
        const y = (gy / (GY - 1) - 0.5) * HEIGHT;
        const r = Math.hypot(x, y) || 1e-4;
        rest[i * 2] = x;
        rest[i * 2 + 1] = y;
        dir[i * 3] = x / r;
        dir[i * 3 + 1] = y / r;
        dir[i * 3 + 2] = r;
        positions[i * 3] = x;
        positions[i * 3 + 1] = y;
        positions[i * 3 + 2] = 0;
        i++;
      }
    }
    return { positions, colors, rest, dir };
  }, []);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const p = scroll.range(
      sceneIndex / SCENE_COUNT,
      (sceneIndex + 1) / SCENE_COUNT,
    );
    const amp = 0.32 + p * 0.25; // displacement magnitude grows a touch on scroll
    const c = new THREE.Color();

    const geo = pointsRef.current?.geometry;
    if (geo) {
      const pos = geo.attributes.position.array;
      const col = geo.attributes.color.array;
      for (let i = 0; i < COUNT; i++) {
        const r = dir[i * 3 + 2];
        const phase = r * K - t * SPEED;
        const falloff = 1 / (1 + r * 0.28);
        const disp = Math.sin(phase) * amp * falloff;
        // Move the particle along its radial direction (longitudinal motion).
        pos[i * 3] = rest[i * 2] + dir[i * 3] * disp;
        pos[i * 3 + 1] = rest[i * 2 + 1] + dir[i * 3 + 1] * disp;
        pos[i * 3 + 2] = 0;
        // Brightness tracks COMPRESSION: where particles bunch toward each other
        // (-cos of the phase is the local density), the medium glows.
        const compression = (-Math.cos(phase) * 0.5 + 0.5) * falloff;
        c.copy(dim).lerp(
          bright,
          THREE.MathUtils.clamp(compression * 1.4, 0, 1),
        );
        col[i * 3] = c.r;
        col[i * 3 + 1] = c.g;
        col[i * 3 + 2] = c.b;
      }
      geo.attributes.position.needsUpdate = true;
      geo.attributes.color.needsUpdate = true;
    }

    // The source pulses in time with the wave it emits.
    if (sourceRef.current) {
      const s = 0.9 + Math.sin(-t * SPEED) * 0.18;
      sourceRef.current.scale.setScalar(s);
    }

    // The tracked particle oscillates along its radial direction about REST.
    if (trackedRef.current) {
      const falloff = 1 / (1 + REST_R * 0.28);
      const disp = Math.sin(REST_R * K - t * SPEED) * amp * falloff;
      trackedRef.current.position.set(
        REST[0] + REST_DIR[0] * disp,
        REST[1] + REST_DIR[1] * disp,
        0.1,
      );
    }
  });

  return (
    <group position={position}>
      <points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={COUNT}
            array={positions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={COUNT}
            array={colors}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          map={sprite}
          size={0.12}
          vertexColors
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          sizeAttenuation
          opacity={0.95}
        />
      </points>

      {/* The vibrating source at the centre of the medium */}
      <mesh ref={sourceRef}>
        <sphereGeometry args={[0.26, 24, 24]} />
        <meshBasicMaterial color="#bdf6ff" />
      </mesh>

      {/* One tracked particle: rest marker, oscillation guide, and the live dot */}
      <mesh position={[REST[0], REST[1], 0.05]}>
        <ringGeometry args={[0.17, 0.21, 24]} />
        <meshBasicMaterial
          color="#ffffff"
          transparent
          opacity={0.35}
          side={THREE.DoubleSide}
        />
      </mesh>
      <Line
        points={[
          [
            REST[0] - REST_DIR[0] * TRACK_RANGE,
            REST[1] - REST_DIR[1] * TRACK_RANGE,
            0.05,
          ],
          [
            REST[0] + REST_DIR[0] * TRACK_RANGE,
            REST[1] + REST_DIR[1] * TRACK_RANGE,
            0.05,
          ],
        ]}
        color="#ffffff"
        lineWidth={1}
        dashed
        dashSize={0.1}
        gapSize={0.08}
        transparent
        opacity={0.4}
      />
      <mesh ref={trackedRef} position={[REST[0], REST[1], 0.1]}>
        <sphereGeometry args={[0.13, 16, 16]} />
        <meshBasicMaterial color="#ffffff" toneMapped={false} />
      </mesh>
      <ResponsiveText
        position={[REST[0] + 0.45, REST[1] + 0.75, 0.1]}
        fontSize={0.2}
        color="#cfe0ec"
        anchorX="left"
        anchorY="middle"
        maxWidth={4}
      >
        one particle — oscillates about its rest point, never travels
      </ResponsiveText>

      <pointLight color="#22aaff" intensity={3} distance={14} />
    </group>
  );
}
